const MAX_SNAPSHOT_LENGTH = 4000;
const TRUNCATION_SUFFIX = "\n[truncated]";

const SKIP_TAGS = new Set([
  "SCRIPT", "STYLE", "NOSCRIPT", "SVG", "PATH", "CIRCLE", "RECT", "LINE",
  "POLYGON", "POLYLINE", "ELLIPSE", "IMG", "VIDEO", "AUDIO", "CANVAS",
  "IFRAME", "BR", "HR",
]);

const MEANINGFUL_TAGS = new Set([
  "H1", "H2", "H3", "H4", "H5", "H6",
  "TR", "TABLE",
  "LI", "DT", "DD",
  "P", "SPAN", "DIV", "SECTION", "ARTICLE", "MAIN", "HEADER", "FOOTER", "NAV",
  "A", "BUTTON", "LABEL", "INPUT", "SELECT", "TEXTAREA",
]);

function isHidden(el: Element): boolean {
  if (el instanceof HTMLElement) {
    if (el.hidden || el.getAttribute("aria-hidden") === "true") return true;
    const computed = window.getComputedStyle(el);
    if (computed.display === "none" || computed.visibility === "hidden") return true;
  }
  return false;
}

function extractText(el: Element, depth: number, lines: string[], charCount: { value: number }): void {
  if (charCount.value >= MAX_SNAPSHOT_LENGTH) return;
  if (SKIP_TAGS.has(el.tagName)) return;
  if (isHidden(el)) return;

  const ariaLabel = el.getAttribute("aria-label");
  const role = el.getAttribute("role");

  if (ariaLabel) {
    const line = `${"  ".repeat(depth)}${ariaLabel}`;
    lines.push(line);
    charCount.value += line.length + 1;
    for (const child of Array.from(el.children)) {
      extractText(child, depth + 1, lines, charCount);
    }
    return;
  }

  if (!MEANINGFUL_TAGS.has(el.tagName) && !role) {
    for (const child of Array.from(el.children)) {
      extractText(child, depth, lines, charCount);
    }
    return;
  }

  const tag = el.tagName;

  if (tag[0] === "H" && tag.length === 2) {
    const text = el.textContent?.trim();
    if (text) {
      const line = `${"  ".repeat(depth)}# ${text}`;
      lines.push(line);
      charCount.value += line.length + 1;
    }
    return;
  }

  if (tag === "TR") {
    const cells = Array.from(el.querySelectorAll(":scope > th, :scope > td"))
      .map((c) => c.textContent?.trim() ?? "")
      .filter(Boolean);
    if (cells.length > 0) {
      const line = `${"  ".repeat(depth)}| ${cells.join(" | ")} |`;
      lines.push(line);
      charCount.value += line.length + 1;
    }
    return;
  }

  if (tag === "TABLE") {
    const line = `${"  ".repeat(depth)}[Table]`;
    lines.push(line);
    charCount.value += line.length + 1;
    const rows = el.querySelectorAll(":scope > tr, :scope > thead > tr, :scope > tbody > tr, :scope > tfoot > tr");
    for (const child of Array.from(rows)) {
      extractText(child, depth + 1, lines, charCount);
    }
    return;
  }

  if (tag === "LI" || tag === "DT" || tag === "DD") {
    const text = el.textContent?.trim();
    if (text) {
      const line = `${"  ".repeat(depth)}- ${text}`;
      lines.push(line);
      charCount.value += line.length + 1;
    }
    return;
  }

  const directText = Array.from(el.childNodes)
    .filter((n) => n.nodeType === Node.TEXT_NODE)
    .map((n) => n.textContent?.trim())
    .filter(Boolean)
    .join(" ");

  if (directText) {
    const line = `${"  ".repeat(depth)}${directText}`;
    lines.push(line);
    charCount.value += line.length + 1;
  }

  for (const child of Array.from(el.children)) {
    extractText(child, depth + 1, lines, charCount);
  }
}

export function getPageSnapshot(): string {
  const lines: string[] = [];
  const charCount = { value: 0 };
  extractText(document.body, 0, lines, charCount);

  let result = lines.join("\n");
  if (result.length > MAX_SNAPSHOT_LENGTH) {
    result = result.slice(0, MAX_SNAPSHOT_LENGTH - TRUNCATION_SUFFIX.length) + TRUNCATION_SUFFIX;
  }
  return result;
}
